import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, TrendingUp, TrendingDown, Zap, Target } from 'lucide-react';
import type { TechnicianPensieveStats, PensieveComboInsight } from '@/hooks/useTechnicianPensieveStats';
import { PassportProvenanceBadge } from './PassportProvenanceBadge';

interface PassportPensieveInsightsProps {
  stats: TechnicianPensieveStats | undefined;
  isLoading: boolean;
}

export function PassportPensieveInsights({ stats, isLoading }: PassportPensieveInsightsProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-6 w-28" />
            <Skeleton className="h-6 w-36" />
          </div>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!stats || !stats.hasData) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            OptiTime Learning
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Still learning – not enough repeated jobs yet to show OptiTime insights.
          </p>
        </CardContent>
      </Card>
    );
  }

  const avgDeltaLabel = stats.avgDeltaPercent !== null
    ? stats.avgDeltaPercent <= 0
      ? `avg ~${Math.abs(Math.round(stats.avgDeltaPercent))}% faster than book`
      : `avg ~${Math.round(stats.avgDeltaPercent)}% slower than book`
    : null;

  const avgDeltaVariant = stats.avgDeltaPercent !== null && stats.avgDeltaPercent <= 0
    ? 'default'
    : 'secondary';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="h-5 w-5 text-primary" />
          OptiTime Learning
        <PassportProvenanceBadge factGroupId="pensieve-learning" className="ml-2" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary chips */}
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className="gap-1">
            <Target className="h-3 w-3" />
            {stats.combosCount} learned combos
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Zap className="h-3 w-3" />
            {stats.totalCompletions} completions
          </Badge>
          {avgDeltaLabel && (
            <Badge variant={avgDeltaVariant} className="gap-1">
              {stats.avgDeltaPercent !== null && stats.avgDeltaPercent <= 0 ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              {avgDeltaLabel}
            </Badge>
          )}
        </div>

        {/* Strength Patterns */}
        {stats.strengths.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-1.5 text-green-600 dark:text-green-400">
              <TrendingUp className="h-4 w-4" />
              Strength Patterns
            </h4>
            <div className="space-y-1.5">
              {stats.strengths.map((combo) => (
                <ComboRow key={combo.id} combo={combo} type="strength" />
              ))}
            </div>
          </div>
        )}

        {/* Growth Edges */}
        {stats.growthAreas.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <TrendingDown className="h-4 w-4" />
              Growth Edges
            </h4>
            <div className="space-y-1.5">
              {stats.growthAreas.map((combo) => (
                <ComboRow key={combo.id} combo={combo} type="growth" />
              ))}
            </div>
          </div>
        )}

        {/* No patterns found message */}
        {stats.strengths.length === 0 && stats.growthAreas.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No significant patterns yet – tech is performing close to book times across learned combos.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ComboRow({ combo, type }: { combo: PensieveComboInsight; type: 'strength' | 'growth' }) {
  const deltaText = type === 'strength'
    ? `${Math.abs(Math.round(combo.deltaPercent))}% faster`
    : `${Math.round(combo.deltaPercent)}% slower`;

  const badgeClass = type === 'strength'
    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';

  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
      <div className="flex-1 min-w-0">
        <span className="font-medium truncate block">{combo.vehicleLabel}</span>
        <span className="text-muted-foreground text-xs">{combo.jobName}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant="outline" className={badgeClass}>
          {deltaText}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {combo.completions} jobs
        </span>
      </div>
    </div>
  );
}
